import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SelectedPresentation from "../../SelectedPresentation";
import InstanceProducts from "../../../pages/instances/connect/products/InstanceProducts";
import useCartWorkspace from "../../../pages/instances/sales/orders/orders-single/useCartWorkspace";
import InvoicingCartAdjustments from "../InvoicingCartAdjustments";

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

export default function InvoiceCartTab({
  orderId,
  order,
  instanceUniqueId,
  invoicingCartKey,
  activeInvoicingCartSummary = null,
  currency = "CAD",
  invoice = null,
  onCartSaved = null,
}) {
  const [showProducts, setShowProducts] = useState(false);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [editingAdjustment, setEditingAdjustment] = useState(null);

  const monetaryEditLock = useMemo(() => {
    if (!invoice) return null;
    const paymentStatus = String(invoice?.paymentStatus || "").trim().toLowerCase();
    const invoiceStatus = String(invoice?.invoiceStatus || "").trim().toLowerCase();
    const accountingPaymentStatus = String(invoice?.accountingPaymentStatus || "").trim().toLowerCase();
    const hasPaymentActivity = (
      Number(invoice?.paymentReceivedAmount || 0) > 0
      || Number(invoice?.accountingPaidAmount || 0) > 0
      || ["paid", "partial", "partially_paid", "partially paid", "completed", "succeeded", "success"].includes(paymentStatus)
      || ["paid", "completed"].includes(invoiceStatus)
      || ["paid", "partial", "partially_paid", "partially paid"].includes(accountingPaymentStatus)
    );
    if (hasPaymentActivity) {
      return "This invoice has payment activity. Monetary cart changes are locked; use the accounting adjustment/credit workflow.";
    }
    // Exported-but-unpaid invoices remain editable. The server performs a live
    // QuickBooks payment preflight before saving and resyncs the same QBO invoice.
    return null;
  }, [invoice]);

  const cartWorkspace = useCartWorkspace({
    orderId,
    order,
    cartView: "invoicing-cart",
    invoicingCartKey,
    requireExplicitInvoicingCartKey: true,
  });

  const persistWorkspaceMutation = useCallback(async ({ nextPayload }) => {
    if (!nextPayload || !invoicingCartKey) return;
    await cartWorkspace.savePayload(nextPayload);
    onCartSaved?.();
  }, [cartWorkspace, invoicingCartKey, onCartSaved]);

  const editableWorkspace = useMemo(() => ({
    ...cartWorkspace,
    editable: cartWorkspace.editable && !monetaryEditLock,
    editReason: monetaryEditLock || cartWorkspace.editReason,
    capabilities: {
      editItem: !monetaryEditLock,
      copyItem: !monetaryEditLock,
      deleteItem: !monetaryEditLock,
      addProduct: !monetaryEditLock,
      addOtherService: !monetaryEditLock,
      sections: !monetaryEditLock,
      reorder: !monetaryEditLock,
      adjustments: !monetaryEditLock,
      orderWorkflow: false,
    },
  }), [cartWorkspace, monetaryEditLock]);

  const updateItemsToCart = useCallback(async (products = []) => {
    const current = cartWorkspace.cartPayload || {};
    const nextPayload = {
      ...current,
      Products: normalizeArray(products),
    };
    cartWorkspace.updateCartPayload(nextPayload);
    await cartWorkspace.savePayload(nextPayload);
    await cartWorkspace.reload();
    onCartSaved?.();
  }, [cartWorkspace, onCartSaved]);

  if (!invoicingCartKey) {
    return (
      <Alert severity="warning">
        This invoice is not linked to an invoicing cart, so there is no editable invoice cart to load.
      </Alert>
    );
  }

  if (cartWorkspace.loading && !cartWorkspace.orderForView) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 240 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {activeInvoicingCartSummary ? (
        <Alert severity="info">
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Editing invoicing cart {activeInvoicingCartSummary.invoicingCartKey || invoicingCartKey}
            {activeInvoicingCartSummary.revisionNumber
              ? ` · Revision ${activeInvoicingCartSummary.revisionNumber}`
              : ""}
            {activeInvoicingCartSummary.itemCount != null
              ? ` · ${activeInvoicingCartSummary.itemCount} item${Number(activeInvoicingCartSummary.itemCount) === 1 ? "" : "s"}`
              : ""}
          </Typography>
          <Typography variant="caption">
            Changes here update the invoice's invoicing cart only. The Sales Order cart remains unchanged.
          </Typography>
        </Alert>
      ) : null}

      {monetaryEditLock ? <Alert severity="warning">{monetaryEditLock}</Alert> : null}
      {cartWorkspace.error ? <Alert severity="error">{cartWorkspace.error}</Alert> : null}
      {cartWorkspace.missing ? (
        <Alert severity="warning">
          The invoicing cart linked to this invoice could not be found.
        </Alert>
      ) : null}

      {!cartWorkspace.missing ? (
        <Box sx={{ maxHeight: "68vh", overflow: "auto", pr: 0.5 }}>
          <SelectedPresentation
            order={cartWorkspace.orderForView}
            fetchOrder={cartWorkspace.reload}
            handleShowProductsPopup={() => setShowProducts(true)}
            cartWorkspace={editableWorkspace}
            onWorkspaceMutationCommitted={persistWorkspaceMutation}
            onAddAdjustment={monetaryEditLock ? null : () => {
              setEditingAdjustment(null);
              setAdjustmentDialogOpen(true);
            }}
          />
          <Box sx={{ mt: 2 }}>
            <InvoicingCartAdjustments
              cartWorkspace={editableWorkspace}
              currency={currency}
              editable={!monetaryEditLock}
              onSaved={onCartSaved}
              dialogOpen={adjustmentDialogOpen}
              initialAdjustment={editingAdjustment}
              onDialogClose={() => {
                setAdjustmentDialogOpen(false);
                setEditingAdjustment(null);
              }}
              onEditRequest={(adjustment) => {
                setEditingAdjustment(adjustment);
                setAdjustmentDialogOpen(true);
              }}
            />
          </Box>
        </Box>
      ) : null}

      <Dialog
        open={showProducts}
        onClose={() => setShowProducts(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Add Product</Typography>
              <Typography variant="caption" color="text.secondary">
                Add directly to this invoice's invoicing cart.
              </Typography>
            </Box>
            <IconButton onClick={() => setShowProducts(false)} aria-label="Close add product dialog">
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, minHeight: "70vh" }}>
          <InstanceProducts
            updateItemsToOrder={updateItemsToCart}
            selectedProducts={normalizeArray(cartWorkspace.orderForView?.Products)}
            setSelectedProducts={() => {}}
            fetchOrder={cartWorkspace.reload}
            isPresentation
            popupMode={showProducts}
            embedded
            closeProductListPopup={() => setShowProducts(false)}
            order={cartWorkspace.orderForView}
            instanceUniqueId={instanceUniqueId}
          />
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
